import { createApi } from "@reduxjs/toolkit/query/react"
import { authQuery } from "./authQuery"
import {
  GraphDto,
  GraphsDto,
  MnemonicData,
  UpdateGraphDto,
  UserGraphDto,
} from "@custom-types/graphs"
import { ProductsGraphDto } from "@custom-types/products"
import { GraphConverter } from "./converter/graphConverter"
import { GraphNodeEdgeData, UnpackedGraph } from "./converter/types"
import { blobToBase64 } from "base64-blob"
import { graphConverterUnpackZip } from "@store/services/converter/unpackZip"
import {
  ScenarioSourceMnemonic,
  ScenarioSourceMnemonicResponse,
  scenarioSourceMnemonicFallback,
} from "@store/services/types"

export type ScenarioSourceType = "forecast" | "scenario" | "historic"

export type GraphParametersImportError = {
  message: {
    human_readable_errors: string[]
    exception_traceback: string
  }
}

export const graphsApi = createApi({
  reducerPath: "graphsApi",
  baseQuery: authQuery,
  tagTypes: ["Graph", "GraphUnpacked", "GraphList", "ScenarioSourceMnemonics", "MnemonicsData"],
  endpoints: (builder) => ({
    getGraphs: builder.query<GraphsDto, void>({
      query: () => ({
        url: "/graphs",
      }),
      providesTags: ["GraphList"],
    }),
    getGraphByProductKey: builder.query<GraphDto, { key: string; orgKey: string; divKey: string }>({
      query: (boy) => ({
        url: `/products/${boy.key}--${boy.orgKey}--${boy.divKey}/graphs`,
      }),
      providesTags: ["Graph"],
    }),
    getGraph: builder.query<GraphDto, string>({
      query: (key) => ({
        url: `/graphs/${encodeURIComponent(key)}`,
      }),
      providesTags: ["Graph"],
    }),
    getUserGraph: builder.query<UserGraphDto, string>({
      query: (key) => ({
        url: `/user-graphs/${encodeURIComponent(key)}`,
      }),
      providesTags: ["Graph"],
    }),
    getUserGraphData: builder.query<GraphNodeEdgeData, string>({
      query: (key) => ({
        url: `/user-graphs/${encodeURIComponent(key)}/data`,
        responseHandler: async (resp) => {
          if (resp.status !== 200) {
            return resp
          }
          const blob = await resp.blob()
          const unpacked = await graphConverterUnpackZip(blob)
          return GraphConverter.convertToReactFlow(unpacked)
        },
      }),
      providesTags: ["Graph"],
    }),
    getGraphData: builder.query<GraphNodeEdgeData, string>({
      query: (key) => ({
        // key example `Global_0.1.0` (familyKey_version)
        url: `/graphs/${encodeURIComponent(key)}/data`,
        responseHandler: async (resp) => {
          if (resp.status !== 200) {
            return resp
          }
          const blob = await resp.blob()
          const unpacked = await graphConverterUnpackZip(blob)
          return GraphConverter.convertToReactFlow(unpacked)
        },
      }),
      providesTags: ["Graph"],
    }),
    getUnpackedGraphData: builder.query<UnpackedGraph, string>({
      query: (key) => ({
        // key example `Global_0.1.0` (familyKey_version)
        url: `/graphs/${encodeURIComponent(key)}/data`,
        responseHandler: async (resp) => {
          if (resp.status !== 200) {
            return resp
          }
          const blob = await resp.blob()
          return await graphConverterUnpackZip(blob)
        },
      }),
      providesTags: (_result, _error, key) => [{ type: "GraphUnpacked", id: key }],
    }),
    updateGraph: builder.mutation<GraphDto, { key: string; body: UpdateGraphDto }>({
      query: ({ key, body }) => ({
        url: `/graphs/${encodeURIComponent(key)}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Graph", "GraphList"],
    }),
    deleteGraph: builder.mutation<void, string>({
      query: (graphKey) => ({
        url: `/graphs/${encodeURIComponent(graphKey)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["GraphList"],
    }),
    exposeGraph: builder.mutation<string, { key: string; contractKey: string }>({
      query: ({ key, contractKey }) => {
        return {
          method: "POST",
          url: `/graphs/${encodeURIComponent(key)}/expose?contract_key=${encodeURIComponent(
            contractKey,
          )}`,
        }
      },
    }),
    getGraphsByProduct: builder.query<
      ProductsGraphDto,
      { key: string; orgKey: string; divKey: string }
    >({
      query: ({ key, orgKey, divKey }) => ({
        url: `/products/${encodeURIComponent(key)}--${orgKey}--${divKey}`,
      }),
      providesTags: ["Graph"],
    }),
    getGraphValidationFile: builder.query<string, string>({
      query: (key) => ({
        url: `/graphs/${encodeURIComponent(key)}/validation_file`,
        responseHandler: "text",
      }),
      providesTags: ["Graph"],
    }),
    getGraphScenarioFile: builder.query<string, string>({
      query: (key) => ({
        url: `/graphs/${encodeURIComponent(key)}/scenario_file`,
        responseHandler: "text",
      }),
      providesTags: ["Graph"],
    }),
    // { [nodeLabel]: lagValue } - keyed by node label (e.g. "base Lag 1"), not the FE-internal reactflow node id.
    getGraphOriginalLagValue: builder.query<Record<string, number | null>, string>({
      query: (key) => ({
        url: `/graphs/${encodeURIComponent(key)}/original-calculated-lag-values`,
      }),
      providesTags: ["Graph"],
    }),
    getScenarioSourceMnemonics: builder.query<
      ScenarioSourceMnemonic[],
      { graphKey: string; type: ScenarioSourceType; startDate?: string; scenarioVersion?: string }
    >({
      query: ({ graphKey, type, startDate }) => ({
        url: `/editor/mnemonics-in-scenario-source?graph_id=${encodeURIComponent(
          graphKey,
        )}&type_hint=${type}${startDate ? `&start_date=${startDate}` : ""}`,
      }),
      transformResponse: (responseData: ScenarioSourceMnemonicResponse[]) =>
        responseData.map(scenarioSourceMnemonicFallback),
      providesTags: ["Graph", "ScenarioSourceMnemonics"],
    }),
    getMnemonisData: builder.query<
      MnemonicData,
      {
        graphKey: string
        type: ScenarioSourceType
        mnemonic: string
        startDate?: string
        scenarioVersion?: string
      }
    >({
      query: ({ graphKey, type, mnemonic, startDate }) => {
        const params = [
          ["graph_id", graphKey],
          ["type_hint", type],
          ["mnemonic", mnemonic],
        ]
        if (startDate) params.push(["start_date", startDate])
        const urlParams = new URLSearchParams(params)

        return { url: `/editor/mnemonics-data?${urlParams.toString()}` }
      },
      // align different formats between 2 endpoints that should respond with the same data structure - getMnemonisData & getGraphForecastMnemonic
      transformResponse: (responseData: Record<string, string | number | null>[]) => {
        const firstRow = responseData[0]
        if (!firstRow) return { empty: true, columns: [], rows: [] }

        const columns: MnemonicData["columns"] = Object.keys(firstRow)
        const rows: MnemonicData["rows"] = responseData.map((responseRow) => {
          const row: MnemonicData["rows"][number] = []

          columns.forEach((column) => row.push(responseRow[column] ?? null))

          return row
        })

        return { empty: false, columns, rows }
      },
      providesTags: ["Graph", "MnemonicsData"],
    }),
    putGraphParameters: builder.mutation<string, { graphKey: string; file: File }>({
      query: ({ graphKey, file }) => {
        const formData = new FormData()
        formData.append("data", file)

        return {
          url: `/graph_params/${encodeURIComponent(graphKey)}/import`,
          method: "PUT",
          body: formData,
          responseHandler: async (resp) => {
            if (resp.status !== 200) return resp
            const blob = await resp.blob()
            const base64 = await blobToBase64(blob)

            return base64
          },
        }
      },
      invalidatesTags: ["Graph", "GraphList"],
    }),
    postGraphParameters: builder.mutation<string, { fileBlob: Blob }>({
      query: ({ fileBlob }) => {
        const formData = new FormData()
        formData.append("data", fileBlob)

        return {
          url: `/tkrisk/parameters_xlsx`,
          method: "POST",
          body: formData,
          responseHandler: async (resp) => {
            if (resp.status !== 200) return resp
            const blob = await resp.blob()
            const base64 = await blobToBase64(blob)

            return base64
          },
        }
      },
      invalidatesTags: ["Graph", "GraphList"],
    }),
    getGraphForecastMnemonic: builder.query<
      MnemonicData,
      { key: string; mnemonic: string; isScenario: boolean }
    >({
      query: ({ key, mnemonic, isScenario }) => {
        const q = isScenario ? `?is_scenario=1` : ""
        return {
          url: `/graphs/${encodeURIComponent(key)}/scenario/${encodeURIComponent(mnemonic)}${q}`,
        }
      },
      providesTags: ["Graph"],
    }),
    exportGraph: builder.query<ArrayBuffer, string>({
      query: (key) => {
        return {
          url: `/graphs/${encodeURIComponent(key)}/export-graph`,
          responseHandler: async (resp) => {
            if (resp.status !== 200) {
              return resp
            }
            return await resp.arrayBuffer()
          },
        }
      },
    }),
  }),
})

export const {
  useGetGraphsQuery,
  useLazyGetGraphByProductKeyQuery,
  useDeleteGraphMutation,
  useUpdateGraphMutation,
  useGetGraphQuery,
  useLazyGetGraphScenarioFileQuery,
  useGetGraphScenarioFileQuery,
  useGetGraphOriginalLagValueQuery,
  useLazyGetGraphValidationFileQuery,
  useGetGraphDataQuery,
  useExposeGraphMutation,
  useGetUnpackedGraphDataQuery,
  useLazyGetUnpackedGraphDataQuery,
  useLazyGetGraphDataQuery,
  useLazyGetGraphQuery,
  usePutGraphParametersMutation,
  usePostGraphParametersMutation,
  useGetUserGraphQuery,
  useGetUserGraphDataQuery,
  useLazyGetUserGraphDataQuery,
  useGetGraphForecastMnemonicQuery,
  useGetScenarioSourceMnemonicsQuery,
  useGetMnemonisDataQuery,
  useLazyExportGraphQuery,
} = graphsApi
